import React from "react";
import { ComponentType } from "@growth-machine-llc/stridist-api-client";
import {
  getAllFilters,
  getFilterStatus,
  mapFilters,
} from "../exercise-library/filters/filterUtils";
import { Box, Button, Divider, Popover, Typography } from "@mui/material";
import { ChevronDownIcon as ArrowDropDown } from "lucide-react";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";
import { ExerciseFiltersContent } from "../exercise-library/filters/ExerciseFiltersContent";
import ResetFiltersButton from "../exercise-library/filters/ResetFiltersButton";

const useStyles = makeStyles((theme) => ({
  root: { fontWeight: "bold", fontSize: 15 },
  active: { color: theme.palette.primary.main },
  inactive: { color: theme.palette.text.primary },
  paper: {
    borderRadius: theme.spacing(1.5),
    minWidth: theme.spacing(32),
    padding: theme.spacing(0),
  },
  header: {
    padding: theme.spacing(0, 1, 0.5, 2),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerText: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    fontWeight: 500,
  },
}));

const getCategoryLabel = (category: ComponentType): string => {
  return category
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");
};

const ComponentTemplateCategoryLabels = Object.values(ComponentType).reduce(
  (acc, category) => {
    acc[category] = getCategoryLabel(category);
    return acc;
  },
  {} as Record<ComponentType, string>,
);

// TODO: Make generic filters for all libraries
export const CategoryFilter = React.memo(function CategoryFilter({
  onChange,
  contextFilters,
  className,
}: {
  onChange?: (selected: ComponentType[]) => void;
  contextFilters?: ComponentType[];
  className?: string;
}) {
  const s = useStyles();
  const ref = React.useRef<HTMLButtonElement>();
  const [open, setOpen] = React.useState(false);

  const [filters, _setFilters] = React.useState(() => {
    const initialFilters = mapFilters(ComponentType, contextFilters);
    const { [ComponentType.MESSAGE]: _, ...rest } = initialFilters;
    return rest;
  });

  const setFilters = React.useCallback(
    (newFilters: Record<ComponentType, boolean>) => {
      const { [ComponentType.MESSAGE]: _, ...rest } = newFilters;
      _setFilters(rest);
    },
    [],
  );

  const isAllSelected = Object.values(filters).every((v) => v);

  const filterStatus = React.useMemo(() => getFilterStatus(filters), [filters]);

  React.useEffect(() => {
    setFilters(mapFilters(ComponentType, contextFilters));
  }, [contextFilters]);

  const extractSelected = React.useCallback(
    (filters: Record<ComponentType, boolean>): ComponentType[] =>
      Object.entries(filters)
        .filter(([_, checked]) => checked)
        .map(([key]) => key as ComponentType),
    [],
  );

  const handleChange = React.useCallback(
    (newFilters: Record<ComponentType, boolean>) => {
      const selected = extractSelected(newFilters);
      onChange?.(selected);
    },
    [extractSelected, onChange],
  );

  return (
    <>
      <Button
        ref={ref}
        className={clsx(
          s.root,
          filterStatus.activeCount ? s.active : s.inactive,
          className,
        )}
        onClick={() => setOpen(true)}
      >
        Type {filterStatus.text}
        <ArrowDropDown size={20} style={{ marginLeft: 2 }} />
      </Button>

      <Popover
        open={open}
        anchorEl={ref.current}
        onClose={() => setOpen(false)}
        classes={{ paper: s.paper }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ zIndex: 99999999, overflowY: "hidden" }}
      >
        <Box className={s.header}>
          <Typography className={s.headerText}>{`Filter by Type`}</Typography>
          <ResetFiltersButton
            disabled={isAllSelected}
            tooltipPlacement="bottom"
            onClick={() => {
              const updatedFilters = getAllFilters(filters, true);
              const selected = extractSelected(updatedFilters as any);
              onChange?.(selected);
            }}
          />
        </Box>
        <Divider />
        <Box sx={{ overflowY: "auto", maxHeight: 450 }}>
          <ExerciseFiltersContent
            filters={filters as any}
            onChange={handleChange}
            filterTypesLabels={ComponentTemplateCategoryLabels}
            noneOptionVisible={false}
          />
        </Box>
      </Popover>
    </>
  );
});
